/**
 * Login Page - עמוד התחברות
 * User login with email and password
 */

import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { LogIn, AlertCircle, ShieldCheck } from 'lucide-react'
import api, { handleApiError } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { AuthResponse, User } from '../types'
import { portalHomePathFor } from '../config/tracks'

const loginSchema = z.object({
  email: z
    .string()
    .email('כתובת אימייל לא תקינה')
    .min(1, 'שדה חובה'),
  password: z.string().min(1, 'שדה חובה'),
})

type LoginFormData = z.infer<typeof loginSchema>

/**
 * Staff (admin/advisor) land on the chat page; clients go to their dashboard.
 *
 * A section admin (מנהל אזור) lands in the admin panel — the chat and the
 * dashboard are the advisory product, which is not what they were given rights to.
 *
 * A portal-only user (signed up at /pensia or /mashkanta, no advisory agreement)
 * goes to their own area instead — the dashboard would bounce them straight into
 * an advisory agreement they never asked for, and their menu has no way back.
 */
const homeRouteFor = (u?: User | null) => {
  if (u?.role === 'sub_admin') return '/admin'
  if (u?.role === 'admin' || u?.role === 'advisor') return '/chat'
  return portalHomePathFor(u) || '/dashboard'
}

/**
 * Honour `?next=` so "כניסה" from /pensia returns there instead of dumping the
 * user on the dashboard. Only same-site absolute paths are accepted — an
 * unvalidated next= is an open-redirect handed to anyone who can send a link.
 */
const safeNext = (next: string | null): string | null =>
  next && /^\/[A-Za-z0-9\-_/]*$/.test(next) && !next.startsWith('//') ? next : null

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('expired') === '1'
  const { login } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Email-2FA (admin/advisor) second step
  const [otpStep, setOtpStep] = useState(false)
  const [otpPhase, setOtpPhase] = useState<'choose' | 'enter'>('choose')
  const [pendingUser, setPendingUser] = useState<User | null>(null)
  const [recipientName, setRecipientName] = useState('')
  const [submittedName, setSubmittedName] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [resendInfo, setResendInfo] = useState<string | null>(null)
  // True when the account owns its 2FA inbox (super admin) — the code was sent to its
  // own address, so there is no identity step to go back to and no name to resend with.
  const [ownInbox, setOwnInbox] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post<AuthResponse>('/auth/login', data)
      const { user, tokens } = response.data

      if (tokens?.accessToken) {
        login(user, tokens.accessToken)
        navigate(safeNext(searchParams.get('next')) || homeRouteFor(user))
      } else if (response.data.requiresMFA) {
        // Privileged (admin/advisor) login — a one-time code is emailed before tokens
        // are issued. An account with its own configured inbox (super admin) gets the
        // code sent immediately; a shared account is first asked WHO is logging in.
        const ownLabel = response.data.otpRecipientLabel
        setPendingUser(user)
        setRecipientName('')
        setSubmittedName('')
        setOtpCode('')
        setOwnInbox(false)
        setOtpPhase('choose')
        setOtpStep(true)

        if (ownLabel) {
          // On failure we fall through to the identity step with the error shown.
          const userId = (user as any)?.id ?? user?._id
          await api.post('/auth/mfa/email/request', { userId })
          setOwnInbox(true)
          setSubmittedName(ownLabel)
          setOtpPhase('enter')
        }
      }
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  // Backend returns the user object keyed as `id` (not `_id`); read it robustly.
  const pendingUserId = (pendingUser as any)?.id ?? pendingUser?._id

  // Step 2a: the user typed who they are — request a code sent to the matched inbox.
  const onChooseRecipient = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pendingUser || !recipientName.trim()) return
    setIsLoading(true)
    setError(null)
    setResendInfo(null)
    try {
      await api.post('/auth/mfa/email/request', {
        userId: pendingUserId,
        recipientName: recipientName.trim(),
      })
      setSubmittedName(recipientName.trim())
      setOtpPhase('enter')
    } catch (err) {
      // Shows the "admin account not connected to email" message on no match.
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const onVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pendingUser) return
    setIsLoading(true)
    setError(null)
    setResendInfo(null)

    try {
      const response = await api.post<AuthResponse>('/auth/mfa/email/verify', {
        userId: pendingUserId,
        code: otpCode,
      })
      const { user, tokens } = response.data
      if (tokens?.accessToken) {
        const resolvedUser = user ?? pendingUser
        login(resolvedUser, tokens.accessToken)
        navigate(homeRouteFor(resolvedUser))
      }
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const onResendOtp = async () => {
    if (!pendingUser || (!ownInbox && !submittedName)) return
    setError(null)
    setResendInfo(null)
    try {
      await api.post('/auth/mfa/email/request', {
        userId: pendingUserId,
        // Own-inbox accounts have no identity to name; the server targets their address.
        ...(ownInbox ? {} : { recipientName: submittedName }),
      })
      setResendInfo('קוד חדש נשלח.')
    } catch (err) {
      setError(handleApiError(err))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="יפתח ונגר"
            className="h-16 mx-auto mb-3"
          />
          <p className="text-sm text-gray-600">יעוץ השקעות ויעוץ פנסיוני אובייקטיבי</p>
          <p className="text-xs text-gray-400 mt-1">אזור אישי - התחברות</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">
            {otpStep ? 'אימות דו-שלבי' : 'התחבר לחשבון'}
          </h2>

          {/* Session Expired */}
          {!otpStep && sessionExpired && (
            <div className="mb-6 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
              <AlertCircle size={18} className="text-amber-500 flex-shrink-0" />
              <span>תוקף החיבור פג. אנא התחבר מחדש.</span>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="alert alert-error mb-6">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* ── Email-2FA (admin/advisor) ───────────────────────────────── */}
          {otpStep ? (
            otpPhase === 'choose' ? (
              /* Step 2a — who is logging in? */
              <>
                <div className="mb-6 flex items-start gap-2 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 text-sm text-primary-800">
                  <ShieldCheck size={18} className="text-primary-500 flex-shrink-0 mt-0.5" />
                  <span>מי מתחבר? הזן את שמך; קוד אימות חד-פעמי יישלח לתיבת הדוא"ל המשויכת.</span>
                </div>

                <form onSubmit={onChooseRecipient} className="space-y-4">
                  <div>
                    <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-2">
                      מי מתחבר?
                    </label>
                    <input
                      id="recipientName"
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className="input"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !recipientName.trim()}
                    className="btn btn-primary w-full"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="spinner" />
                        <span>שולח...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <ShieldCheck size={20} />
                        <span>שלח קוד אימות</span>
                      </div>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpStep(false)
                      setPendingUser(null)
                      setOtpCode('')
                      setError(null)
                      setResendInfo(null)
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    חזרה להתחברות
                  </button>
                </div>
              </>
            ) : (
              /* Step 2b — enter the emailed code */
              <>
                <div className="mb-6 flex items-start gap-2 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 text-sm text-primary-800">
                  <ShieldCheck size={18} className="text-primary-500 flex-shrink-0 mt-0.5" />
                  <span>
                    נשלח קוד אימות חד-פעמי לתיבת הדוא"ל {ownInbox ? 'שלך' : `של ${submittedName}`}.
                    יש להזין אותו כדי להשלים את הכניסה.
                  </span>
                </div>

                {resendInfo && (
                  <div className="mb-6 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800">
                    <ShieldCheck size={18} className="text-green-500 flex-shrink-0" />
                    <span>{resendInfo}</span>
                  </div>
                )}

                <form onSubmit={onVerifyOtp} className="space-y-4">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                      קוד אימות
                    </label>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="input text-center tracking-[0.5em] text-lg"
                      placeholder="______"
                      dir="ltr"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otpCode.length !== 6}
                    className="btn btn-primary w-full"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="spinner" />
                        <span>מאמת...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <ShieldCheck size={20} />
                        <span>אמת והתחבר</span>
                      </div>
                    )}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={onResendOtp}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    שליחת קוד מחדש
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Own-inbox accounts never saw an identity step — go back to login.
                      if (ownInbox) {
                        setOtpStep(false)
                        setPendingUser(null)
                        setOwnInbox(false)
                      }
                      setOtpPhase('choose')
                      setOtpCode('')
                      setError(null)
                      setResendInfo(null)
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {ownInbox ? 'חזרה להתחברות' : 'חזרה לבחירת משתמש'}
                  </button>
                </div>
              </>
            )
          ) : (
          <>
          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                אימייל
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder="example@email.com"
                autoComplete="email"
                dir="ltr"
              />
              {errors.email && (
                <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה
              </label>
              <input
                {...register('password')}
                type="password"
                id="password"
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-left">
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                שכחת סיסמה?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner" />
                  <span>מתחבר...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <LogIn size={20} />
                  <span>התחבר</span>
                </div>
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              אין לך חשבון?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                הירשם עכשיו
              </Link>
            </p>
            <p className="text-gray-600 mt-2">
              תלמיד/ה בקורס?{' '}
              <Link to="/student-login" className="text-primary-600 hover:text-primary-700 font-medium">
                התחברות תלמידים
              </Link>
            </p>
          </div>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>יפתח ונגר יועץ השקעות</p>
          <p className="mt-1">© 2026 כל הזכויות שמורות</p>
        </div>
      </div>
    </div>
  )
}
